import React, { useState, useEffect } from "react";
import { Comment } from "../../types";
import { useAuthStore } from "../../store/authStore";

interface CommentMarkerProps {
  comment: Comment;
  onClick: () => void;
  isCommentMode: boolean;
  activeTab: string;
  isHighlighted?: boolean;
}

export const CommentMarker: React.FC<CommentMarkerProps> = ({
  comment,
  onClick,
  isCommentMode,
  activeTab,
  isHighlighted = false,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const { user, isGuest } = useAuthStore();

  const isAuthenticated = user || isGuest;
  const currentPageUrl = window.location.href;

  // Check if this comment belongs to the current page
  const isOnCurrentPage = comment.position?.pageUrl === currentPageUrl;

  // Don't show comments from other pages
  if (!isOnCurrentPage) {
    return null;
  }

  useEffect(() => {
    const updatePosition = () => {
      if (
        comment.position?.pageX !== undefined &&
        comment.position?.pageY !== undefined
      ) {
        const isFixedElement = comment.position?.isFixedElement;

        if (isFixedElement) {
          // For fixed elements, position relative to viewport
          setPosition({
            x: comment.position.x || comment.position.pageX,
            y: comment.position.y || comment.position.pageY,
          });
        } else {
          // For regular elements, convert page coordinates to viewport coordinates
          // considering current scroll position
          const currentScrollX = window.scrollX || window.pageXOffset;
          const currentScrollY = window.scrollY || window.pageYOffset;

          const calculatedX = comment.position.pageX - currentScrollX;
          const calculatedY = comment.position.pageY - currentScrollY;

          setPosition({
            x: calculatedX,
            y: calculatedY,
          });
        }
      } else {
        // Fallback for old comments without pageX/pageY
        setPosition({
          x: comment.position?.x || 0,
          y: comment.position?.y || 0,
        });
      }
    };

    updatePosition();

    // Update position when scrolling for regular elements
    const handleScroll = () => {
      if (!comment.position?.isFixedElement) {
        updatePosition();
      }
    };

    // Update on resize too
    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [comment.position]);

  // For regular elements using fixed positioning, just check viewport bounds
  const isFixedElement = comment.position?.isFixedElement;
  const isVisible =
    position.x >= -50 &&
    position.x <= window.innerWidth + 50 &&
    position.y >= -50 &&
    position.y <= window.innerHeight + 50;

  if (
    !isCommentMode ||
    !isVisible ||
    (activeTab === "active" && comment.status === "resolved") ||
    (activeTab === "resolved" && comment.status === "active")
  ) {
    return null;
  }

  // Use fixed positioning for both types - the coordinates are calculated differently
  const positionStyle = {
    position: "fixed" as const,
    left: position.x,
    top: position.y,
    transform: "translate(-50%, -50%)",
  };

  return (
    <div
      className="feedbackflow-comment-marker"
      style={{
        ...positionStyle,
        zIndex: 2147483646, // Just below cursor
      }}
      onMouseEnter={() => isAuthenticated && setShowPreview(true)}
      onMouseLeave={() => isAuthenticated && setShowPreview(false)}
      onClick={isAuthenticated ? onClick : undefined}
      data-comment-marker
    >
      <div
        className={`relative ${
          isAuthenticated ? "cursor-pointer" : "cursor-default"
        }`}
      >
        {/* Comment marker with highest z-index */}
        <div className="w-6 h-6 relative">
          <div
            className={`w-full h-full rounded-full flex items-center justify-center text-xs font-medium shadow-lg ring-2 transition-all duration-300 ${
              isHighlighted
                ? "bg-yellow-500 ring-yellow-300 animate-pulse scale-110"
                : "bg-indigo-600 ring-white"
            }`}
            style={{ color: "#ffffff" }}
          >
            {comment.number}
          </div>

          {/* Highlight pulse effect */}
          {isHighlighted && (
            <div className="absolute inset-0 rounded-full bg-yellow-400 opacity-30 animate-ping" />
          )}
        </div>

        {/* Unread indicator */}
        {isAuthenticated && !comment.isRead && comment.status === "active" && (
          <div
            className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"
            style={{ zIndex: 2147483647 }}
          />
        )}

        {/* Quick preview on hover */}
        {isAuthenticated && showPreview && (
          <div
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2"
            style={{ zIndex: 2147483647 }}
          >
            <div
              className="bg-gray-900 text-xs px-2 py-1 rounded shadow-lg max-w-xs whitespace-nowrap overflow-hidden text-ellipsis"
              style={{ color: "#ffffff" }}
            >
              {comment.text}
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900" />
          </div>
        )}
      </div>
    </div>
  );
};
